/**
 * Pointer only — invite/login return and soft deep links.
 * Hub URLs (`/scenarios`, `/scenarios/templates`) do not auto-open this id.
 * Cleared when the board is missing or no longer visible to the account.
 */
export const LAST_SERVER_INSTANCE_KEY = 'buxus.sc.lastInstanceId'

/**
 * Last open board: library mode, template, and optional session behind Back.
 * Stored for other contexts — Studio boot does not redirect hubs from this.
 * Cleared when the remembered session id is gone / unauthorized.
 */
export const LAST_STUDIO_VIEW_KEY = 'buxus.sc.view'

/** Scenario Studio app path (no trailing slash). */
export const SCENARIOS_PATH = '/scenarios'

/** Template library prefix under Studio (`/scenarios/templates/...`). */
export const SCENARIOS_TEMPLATES_PATH = `${SCENARIOS_PATH}/templates`

/** @deprecated Prefer path `/scenarios/<uuid>`; still accepted once then redirected. */
export const STUDIO_BOARD_QUERY = 'board'

const STUDIO_SESSION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Shareable / bookmarkable session URL path. */
export function studioSessionPath(instanceId: string): string {
  return `${SCENARIOS_PATH}/${instanceId.trim()}`
}

const POST_LOGIN_STUDIO_PATH_KEY = 'buxus-web-post-login-studio-path'

/**
 * Sign-in always returns to the site root. Remember a session to open once that return is signed in.
 * Left in place until the session route is reached, so a render can read it more than once.
 */
export function rememberPostLoginStudioPath(instanceId: string): void {
  try {
    sessionStorage.setItem(POST_LOGIN_STUDIO_PATH_KEY, studioSessionPath(instanceId))
  } catch {
    /* ignore private mode */
  }
}

export function peekPostLoginStudioPath(): string | null {
  try {
    const value = sessionStorage.getItem(POST_LOGIN_STUDIO_PATH_KEY)
    if (!value) return null
    const id = value.slice(`${SCENARIOS_PATH}/`.length)
    if (value === studioSessionPath(id) && STUDIO_SESSION_ID_RE.test(id)) return value
    return null
  } catch {
    return null
  }
}

export function clearPostLoginStudioPath(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_STUDIO_PATH_KEY)
  } catch {
    /* ignore private mode */
  }
}

/** Shareable / bookmarkable template URL path. */
export function studioTemplatePath(templateId: string): string {
  return `${SCENARIOS_TEMPLATES_PATH}/${encodeURIComponent(templateId.trim())}`
}

function normalizePathname(pathname: string): string {
  const trimmed = pathname.trim() || '/'
  return trimmed !== '/' && trimmed.endsWith('/') ? trimmed.replace(/\/+$/, '') : trimmed
}

/** Parse `/scenarios/<uuid>` → id, or '' when not a session deep link. */
export function parseStudioSessionId(pathname: string): string {
  const n = normalizePathname(pathname)
  if (n === SCENARIOS_PATH || n === SCENARIOS_TEMPLATES_PATH || n.startsWith(`${SCENARIOS_TEMPLATES_PATH}/`)) {
    return ''
  }
  const prefix = `${SCENARIOS_PATH}/`
  if (!n.startsWith(prefix)) return ''
  const id = n.slice(prefix.length).split('/')[0]?.trim() ?? ''
  return STUDIO_SESSION_ID_RE.test(id) ? id : ''
}

/** Parse `/scenarios/templates/<id>` → id, or '' when not a template deep link. */
export function parseStudioTemplateId(pathname: string): string {
  const n = normalizePathname(pathname)
  const prefix = `${SCENARIOS_TEMPLATES_PATH}/`
  if (!n.startsWith(prefix)) return ''
  const raw = n.slice(prefix.length).split('/')[0]?.trim() ?? ''
  if (!raw) return ''
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

/** True for `/scenarios/templates` (library hub, no specific template). */
export function isStudioTemplatesHubPath(pathname: string): boolean {
  return normalizePathname(pathname) === SCENARIOS_TEMPLATES_PATH
}

/**
 * How the user entered Studio this tab (sessionStorage — survives login returnTo, not a new tab).
 * - `board`: invite / Share / QR deep link to a session id.
 * - `template`: deep link to a template id.
 */
export type StudioArrivalSource = 'board' | 'template'

export type StudioArrival = {
  source: StudioArrivalSource
  /** Instance or template id the link targeted. */
  targetId: string
}

export const STUDIO_ARRIVAL_KEY = 'buxus.sc.arrival'

/** Remember the board from an email/share link so login and a later Training visit reopen it. */
export function rememberStudioBoard(instanceId: string): void {
  const id = instanceId.trim()
  if (!id) return
  try {
    localStorage.setItem(LAST_SERVER_INSTANCE_KEY, id)
    localStorage.setItem(
      LAST_STUDIO_VIEW_KEY,
      JSON.stringify({ library: false, templateId: '', instanceId: id }),
    )
  } catch {
    /* private mode */
  }
}

/** Remember a template deep link for boot / login return. */
export function rememberStudioTemplate(templateId: string): void {
  const id = templateId.trim()
  if (!id) return
  try {
    localStorage.setItem(
      LAST_STUDIO_VIEW_KEY,
      JSON.stringify({ library: true, templateId: id, instanceId: '' }),
    )
  } catch {
    /* private mode */
  }
}

/** Mark this tab’s entry as a deep link so lose-access can avoid an unmotivated session jump. */
export function rememberStudioArrival(source: StudioArrivalSource, targetId: string): void {
  const id = targetId.trim()
  if (!id) return
  try {
    sessionStorage.setItem(STUDIO_ARRIVAL_KEY, JSON.stringify({ source, targetId: id } satisfies StudioArrival))
  } catch {
    /* private mode */
  }
}

export function readStudioArrival(): StudioArrival | null {
  try {
    const raw = sessionStorage.getItem(STUDIO_ARRIVAL_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StudioArrival>
    if (
      (parsed.source === 'board' || parsed.source === 'template') &&
      typeof parsed.targetId === 'string' &&
      parsed.targetId.trim()
    ) {
      return { source: parsed.source, targetId: parsed.targetId.trim() }
    }
    return null
  } catch {
    return null
  }
}

export function clearStudioArrival(): void {
  try {
    sessionStorage.removeItem(STUDIO_ARRIVAL_KEY)
  } catch {
    /* private mode */
  }
}

/** Legacy `?board=` only — prefer {@link parseStudioSessionId}. */
export function readStudioBoardQuery(search: string): string {
  const id = new URLSearchParams(search).get(STUDIO_BOARD_QUERY)?.trim() ?? ''
  return STUDIO_SESSION_ID_RE.test(id) ? id : ''
}
